import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend } from "recharts";
import { BadgeCheck, Ear, MessageSquare, Lock, Reply, Trash2, Star, TrendingUp, Building2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TRAITS } from "@/lib/traits";
import TrajectoryStrip from "@/components/TrajectoryStrip";
import { toast } from "sonner";

const scoreCls = (s) => (s >= 4 ? "text-emerald-600" : s >= 3 ? "text-amber-600" : s > 0 ? "text-rose-600" : "text-slate-400");

function Kpi({ label, value, icon: Icon, sub, testid, cls = "" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testid}>
      <p className="font-mono text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Icon size={13} className="text-emerald-600" /> {label}</p>
      <p className={`font-display text-4xl mt-2 ${cls}`} style={{ fontWeight: 900 }}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function ReviewRow({ r, onChanged }) {
  const [text, setText] = useState(r.response?.text || "");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (text.trim().length < 10) return toast.error("Response should be at least 10 characters");
    setBusy(true);
    try {
      await api.post(`/reviews/${r.id}/response`, { text });
      toast.success(r.response ? "Response updated" : "Response published");
      setEditing(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/reviews/${r.id}/response`); toast.success("Response removed"); setText(""); onChanged(); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`emp-review-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} fill={n <= r.overall ? "#F59E0B" : "transparent"} color={n <= r.overall ? "#F59E0B" : "#CBD5E1"} />)}
            <span className="font-display text-lg text-slate-900" style={{ fontWeight: 700 }}>{r.text_hidden && !r.title ? <i className="text-slate-400 text-base">Ratings-only review</i> : r.title}</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">About <Link to={`/supervisor/${r.supervisor_id}`} className="text-emerald-700 hover:underline font-medium">{r.supervisor_name}</Link> · {r.period}{r.tags?.length ? ` · ${r.tags.join(", ")}` : ""}</p>
        </div>
        {!r.response && <span className="text-[11px] font-mono uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`emp-review-pending-${r.id}`}>Awaiting response</span>}
      </div>
      {r.comment && <p className="text-sm text-slate-700 mt-3 leading-relaxed">{r.comment}</p>}
      {(r.cons || r.leadership_improvement || r.management_improvement) && (
        <p className="text-xs text-slate-500 mt-2"><b>Improve:</b> {[r.cons, r.leadership_improvement, r.management_improvement].filter(Boolean).join(" · ")}</p>
      )}
      {r.response && !editing ? (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid={`emp-response-${r.id}`}>
          <p className="text-xs font-mono uppercase tracking-wider text-emerald-700 mb-1 flex items-center gap-1"><Reply size={12} /> {r.response.author_name}</p>
          <p className="text-sm text-slate-700">{r.response.text}</p>
          <div className="flex gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="h-7 rounded-full text-xs" data-testid={`emp-edit-response-${r.id}`}>Edit</Button>
            <Button size="sm" variant="ghost" onClick={remove} disabled={busy} className="h-7 rounded-full text-xs text-rose-600" data-testid={`emp-delete-response-${r.id}`}><Trash2 size={12} className="mr-1" /> Remove</Button>
          </div>
        </div>
      ) : (
        <div className="mt-3">
          {editing || !r.response ? (
            <>
              <Textarea data-testid={`emp-response-input-${r.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Respond on behalf of the company. Acknowledge, don't argue — this is public." className="text-sm" />
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={save} disabled={busy} className="h-8 rounded-full bg-slate-900 hover:bg-emerald-600" data-testid={`emp-save-response-${r.id}`}>{busy ? "Saving..." : r.response ? "Update response" : "Publish response"}</Button>
                {editing && <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-8 rounded-full">Cancel</Button>}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function EmployerDashboard() {
  const { user, setAuthOpen } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState(null);
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("pending");

  useEffect(() => {
    if (!user) { setCompanies(null); return; }
    api.get("/employer/companies").then((r) => { setCompanies(r.data); if (r.data[0] && !selected) setSelected(r.data[0].name); });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => selected && api.get(`/employer/companies/${encodeURIComponent(selected)}/dashboard`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-5 py-20 text-center" data-testid="employer-signin-prompt">
        <Building2 size={44} className="mx-auto text-slate-300 mb-4" />
        <h1 className="font-display text-4xl text-slate-900" style={{ fontWeight: 900 }}>Employer Dashboard</h1>
        <p className="text-slate-500 mt-3">Sign in with the email you used at checkout to manage your claimed company.</p>
        <Button data-testid="employer-signin-btn" onClick={() => setAuthOpen(true)} className="mt-6 bg-slate-900 hover:bg-emerald-600 rounded-full px-7">Sign in</Button>
      </div>
    );
  }
  if (companies === null) return <div className="max-w-6xl mx-auto px-5 py-16 text-slate-400 font-mono text-sm">Loading...</div>;
  if (companies.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-5 py-20 text-center" data-testid="employer-none">
        <BadgeCheck size={44} className="mx-auto text-slate-300 mb-4" />
        <h1 className="font-display text-4xl text-slate-900" style={{ fontWeight: 900 }}>No claimed company yet</h1>
        <p className="text-slate-500 mt-3">Claim your company's profile to respond to reviews and see your managers' trends in one place. Make sure to check out with the email on this account ({user.email}).</p>
        <Button data-testid="employer-claim-btn" onClick={() => navigate("/pricing")} className="mt-6 bg-slate-900 hover:bg-emerald-600 rounded-full px-7">See plans</Button>
      </div>
    );
  }

  const reviews = data ? data.reviews.filter((r) => filter === "all" || (filter === "pending" ? !r.response : !!r.response)) : [];

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="employer-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-1 flex items-center gap-1.5"><BadgeCheck size={13} /> Claimed Profile · Employer Dashboard</p>
          <h1 className="font-display text-4xl sm:text-5xl text-slate-900" style={{ fontWeight: 900 }}>{selected}</h1>
          {data && <p className="text-slate-500 mt-1 text-sm">Plan active until {new Date(data.company.plan_active_until).toLocaleDateString()} · <Link to={`/company/${encodeURIComponent(selected)}`} className="text-emerald-700 hover:underline inline-flex items-center gap-1">Public page <ExternalLink size={12} /></Link></p>}
        </div>
        {companies.length > 1 && (
          <select data-testid="employer-company-select" value={selected} onChange={(e) => setSelected(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm">
            {companies.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        )}
      </div>

      {!data ? <p className="text-slate-400 font-mono text-sm">Loading dashboard...</p> : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi testid="kpi-speakup" label="Speak-Up score" icon={Ear} value={data.speak_up_score ? data.speak_up_score.toFixed(1) : "—"} cls={scoreCls(data.speak_up_score)} sub="Open to Feedback, avg of visible managers" />
            <Kpi testid="kpi-pending" label="Awaiting response" icon={MessageSquare} value={data.pending_responses} sub={`${data.reviews.length} visible reviews`} cls={data.pending_responses ? "text-amber-600" : "text-emerald-600"} />
            <Kpi testid="kpi-managers" label="Managers listed" icon={Building2} value={data.supervisors.length} sub={data.hidden_supervisors ? `${data.hidden_supervisors} below the ${data.min_reviews}-review threshold` : "all above threshold"} />
            <Kpi testid="kpi-redflag" label="Top red flag" icon={TrendingUp} value={data.top_tags[0]?.tag || "—"} sub={data.top_tags[0] ? `flagged ${data.top_tags[0].count}×` : "no flags yet"} cls="text-rose-600 !text-2xl mt-3" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mt-6">
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-1 flex items-center gap-1.5"><TrendingUp size={13} /> Quarterly trend</p>
              <p className="text-sm text-slate-500 mb-4">Company-wide overall and Open to Feedback, quarters with enough reviews only.</p>
              {data.trend.length >= 2 ? (
                <div style={{ height: 260 }} data-testid="employer-trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.trend} margin={{ top: 5, right: 20, bottom: 5, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="period" tick={{ fontSize: 12, fill: "#64748B" }} />
                      <YAxis domain={[0, 5]} tick={{ fontSize: 12, fill: "#64748B" }} />
                      <RTooltip /><Legend />
                      <Line type="monotone" dataKey="overall" name="Overall" stroke="#059669" strokeWidth={3} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="open_to_feedback" name="Open to Feedback" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="text-sm text-slate-400" data-testid="employer-trend-empty">Trends appear once two or more quarters each have {3}+ reviews.</p>}
            </div>
            <div>
              <TrajectoryStrip data={data.trajectory ? { ...data.trajectory, available: true, latest_period: "" } : null} />
              <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6" data-testid="employer-top-tags">
                <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-3">Behavior flags</p>
                {data.top_tags.length === 0 ? <p className="text-sm text-slate-400">No flags yet.</p> : data.top_tags.map((t) => (
                  <div key={t.tag} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0"><span className="text-slate-700">{t.tag}</span><span className="font-mono text-rose-600 font-bold">×{t.count}</span></div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6 overflow-x-auto" data-testid="employer-managers-table">
            <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-4">Managers</p>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 font-mono uppercase tracking-wider"><th className="pb-2">Manager</th><th className="pb-2">Reviews</th><th className="pb-2">Overall</th>{TRAITS.map((t) => <th key={t.key} className="pb-2 hidden lg:table-cell">{t.short.split(" ")[0]}</th>)}</tr></thead>
              <tbody>
                {data.supervisors.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100" data-testid={`emp-manager-${s.id}`}>
                    <td className="py-2.5"><Link to={`/supervisor/${s.id}`} className="font-semibold text-slate-900 hover:text-emerald-700">{s.name}</Link><div className="text-xs text-slate-400">{s.title}</div></td>
                    {s.ratings_hidden ? <td colSpan={2 + TRAITS.length} className="py-2.5 text-xs text-slate-400"><Lock size={12} className="inline mr-1" /> Hidden until {data.min_reviews} reviews (anonymity protection)</td> : (
                      <>
                        <td className="py-2.5">{s.review_count}</td>
                        <td className={`py-2.5 font-bold ${scoreCls(s.overall)}`}>{s.overall.toFixed(1)}</td>
                        {TRAITS.map((t) => <td key={t.key} className={`py-2.5 hidden lg:table-cell ${scoreCls(s.traits[t.key])}`}>{s.traits[t.key] ? s.traits[t.key].toFixed(1) : "—"}</td>)}
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-display text-3xl text-slate-900" style={{ fontWeight: 800 }}>Reviews</h2>
              <div className="flex gap-1.5" data-testid="employer-review-filter">
                {[["pending", "Awaiting response"], ["answered", "Answered"], ["all", "All"]].map(([k, l]) => (
                  <button key={k} data-testid={`employer-filter-${k}`} onClick={() => setFilter(k)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}>{l}</button>
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-400 mb-4">Responses are public and shown as "{selected} (employer)". Reviewers are notified when you respond. You never see who wrote a review.</p>
            <div className="space-y-4" data-testid="employer-reviews">
              {reviews.length === 0 ? <p className="text-slate-400 text-sm bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center">Nothing here.</p> : reviews.map((r) => <ReviewRow key={r.id} r={r} onChanged={load} />)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
